/**
 * Shared opportunity card — the durable piece the search list renders (reuse, not
 * copy; the detail surface reuses the label maps + provenance helpers). Stage +
 * user verdict are legible as registered `is-*` classes; the MatchTier chip is
 * HUMAN-LABELLED with its score meaning (AX-6, never a bare float); bookmark /
 * dismiss / delete / open-opportunity are `data-act` verbs matching the api /
 * command names (AX-3), each carrying the `data-opportunity` urn payload. The host
 * element owns the handlers. The deterministic one-liner renders on verify and the
 * authored brief UPGRADES it in place once Briefed arrives.
 */

import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import type { CareerI18n, CareerKey } from "../../i18n"
import type {
  JobState,
  MatchTier,
  OpportunityCardView,
  OpportunityStage,
  OrgEvidenceView,
  VerifyFit,
} from "../domain"

export const TIER_KEY: Record<MatchTier, CareerKey> = {
  strong: "opportunity.tier.strong",
  viable: "opportunity.tier.viable",
  weak: "opportunity.tier.weak",
  unsuited: "opportunity.tier.unsuited",
}
export const STAGE_KEY: Record<OpportunityStage, CareerKey> = {
  discovered: "opportunity.stage.discovered",
  verified: "opportunity.stage.verified",
  briefed: "opportunity.stage.briefed",
  matched: "opportunity.stage.matched",
}
export const FIT_KEY: Record<VerifyFit, CareerKey> = {
  match: "opportunity.fit.match",
  borderline: "opportunity.fit.borderline",
  mismatch: "opportunity.fit.mismatch",
}
export const JOB_STATE_KEY: Record<JobState, CareerKey> = {
  open: "opportunity.jobState.open",
  closed: "opportunity.jobState.closed",
  unknown: "opportunity.jobState.unknown",
}
export const DIMENSION_KEY: Record<string, CareerKey> = {
  fit: "opportunity.dimension.fit",
  social: "opportunity.dimension.social",
  motivational: "opportunity.dimension.motivational",
  goal: "opportunity.dimension.goal",
}

/** 0..1 → a whole-percent integer (AX-6: numbers ship with meaning). */
export const pct = (unit: number): number => Math.round(Math.max(0, Math.min(1, unit)) * 100)

/** The bare opportunity id behind a `urn:career:opportunity:{id}` urn (route param). */
export const opportunityShortId = (urn: string): string => urn.replace(/^urn:career:opportunity:/, "")

/** The match-tier chip, human-labelled with its score meaning. */
export function tierChip(o: OpportunityCardView, t: CareerI18n["t"]): TemplateResult | typeof nothing {
  if (!o.tier) return nothing
  return html`<span class=${classMap({ "opportunity-tier": true, chip: true, [`is-${o.stage}`]: true })} lang="de">
    ${t(TIER_KEY[o.tier])}${typeof o.score === "number" ? html` · <span class="opportunity-score">${t("opportunity.tier.score", { n: pct(o.score) })}</span>` : nothing}
  </span>`
}

/** The verify chips (fit + job state + salary) — RAW, human-labelled. */
function verifyChips(o: OpportunityCardView, t: CareerI18n["t"]): TemplateResult {
  return html`
    ${o.fit ? html`<span class="opportunity-fit chip" lang="de">${t(FIT_KEY[o.fit])}</span>` : nothing}
    ${o.jobState ? html`<span class="opportunity-jobstate chip" lang="de">${t(JOB_STATE_KEY[o.jobState])}</span>` : nothing}
    ${o.salary ? html`<span class="opportunity-salary chip" lang="de">${t("opportunity.salary")}: ${o.salary}</span>` : nothing}
  `
}

/**
 * The lead line: the authored brief once present (UPGRADES in place); else the
 * deterministic verify one-liner; else the raw posting snippet (domain data).
 */
function leadLine(o: OpportunityCardView, t: CareerI18n["t"]): TemplateResult | typeof nothing {
  if (o.brief) {
    return html`<div class=${classMap({ "opportunity-brief": true, "is-degraded": o.brief.degraded })}>
      ${o.brief.degraded ? html`<p class="opportunity-degraded-cue" role="status" lang="de">${t("opportunity.brief.degraded")}</p>` : nothing}
      <p class="opportunity-brief-text" lang="de">${o.brief.brief}</p>
      ${o.brief.fitNote ? html`<p class="opportunity-fitnote" lang="de">${o.brief.fitNote}</p>` : nothing}
    </div>`
  }
  if (o.fit || o.jobState) {
    return html`<p class="opportunity-line" lang="de">${t("opportunity.line.verified", {
      fit: t(o.fit ? FIT_KEY[o.fit] : "opportunity.fit.borderline"),
      state: t(o.jobState ? JOB_STATE_KEY[o.jobState] : "opportunity.jobState.unknown"),
    })}</p>`
  }
  return o.snippet ? html`<p class="opportunity-snippet" lang="de">${o.snippet}</p>` : nothing
}

/** Known employer-evidence kinds → their human source-badge label; unknown kinds show the raw kind. */
export const ORG_SOURCE_KEY: Record<string, CareerKey> = {
  kununu: "org.source.kununu",
  trustpilot: "org.source.trustpilot",
  company: "org.source.company",
  news: "org.source.news",
  posting: "org.source.posting",
}

/** One graded employer signal row: source badge · claim · attributed source (linked when a url is cited). */
function orgSignalRow(e: OrgEvidenceView, t: CareerI18n["t"]): TemplateResult {
  const key = ORG_SOURCE_KEY[e.kind]
  return html`<li class="opportunity-org-signal">
    <span class="org-source-badge" data-kind=${e.kind} lang="de">${key ? t(key) : e.kind}</span>
    <span class="org-signal-claim" lang="de">${e.claim}</span>
    ${e.url
      ? html`<a class="org-signal-source" href=${e.url} target="_blank" rel="noopener noreferrer" lang="de">${e.source}</a>`
      : html`<span class="org-signal-source" lang="de">${e.source}</span>`}
  </li>`
}

/**
 * The disclosable "Arbeitgeber" section (E3b): collapsed shows the synthesized one-line org
 * summary (keeps the list scannable); expanded shows the graded employer signals with source
 * badges. Native `<details>` disclosure (workshop/metadata idiom). Renders NOTHING when the
 * result carries no employer data — a gap is never dressed up as a finding.
 */
function orgDisclosure(o: OpportunityCardView, t: CareerI18n["t"]): TemplateResult | typeof nothing {
  const org = o.org
  if (!org || (!org.summary && org.evidence.length === 0)) return nothing
  return html`
    <details class="opportunity-org-disclose">
      <summary class="opportunity-org-summary-line">
        <span class="opportunity-org-label" lang="de">${t("org.employer.label")}</span>
        <span class="opportunity-org-oneliner" lang="de">${org.summary ?? org.name}</span>
      </summary>
      ${org.evidence.length > 0
        ? html`<ul class="opportunity-org-signals" role="list">
            ${org.evidence.map((e) => orgSignalRow(e, t))}
          </ul>`
        : html`<p class="opportunity-org-empty surface-empty" lang="de">${t("org.signals.empty")}</p>`}
    </details>
  `
}

/** The user-verdict actions — bookmark (or saved chip) · dismiss · delete. */
function verdictActions(o: OpportunityCardView, t: CareerI18n["t"]): TemplateResult {
  return html`
    <div class="opportunity-actions">
      ${o.bookmarked
        ? html`<span class="opportunity-saved chip is-bookmarked" lang="de">${t("opportunity.bookmarked")}</span>`
        : html`<button data-act="bookmark" data-opportunity=${o.urn} lang="de">${t("opportunity.bookmark")}</button>`}
      <button data-act="dismiss" data-opportunity=${o.urn} class="opportunity-dismiss" lang="de">${t("opportunity.dismiss")}</button>
      <button data-act="delete" data-opportunity=${o.urn} class="opportunity-remove" lang="de">${t("opportunity.delete")}</button>
    </div>
  `
}

/** Render one opportunity as an `<li urn=…>` card (AX-4 urn-addressed). */
export function renderOpportunityCard(o: OpportunityCardView, t: CareerI18n["t"]): TemplateResult {
  const shortId = opportunityShortId(o.id)
  return html`
    <li
      urn=${o.urn}
      data-opportunity=${o.urn}
      class=${classMap({ "opportunity-card": true, [`is-${o.stage}`]: true, "is-bookmarked": o.bookmarked })}
    >
      <div class="opportunity-head">
        <a
          class="opportunity-title"
          href=${`/search/opportunities/${shortId}`}
          data-act="open-opportunity"
          data-opportunity=${o.urn}
          lang="de"
          >${o.title}</a
        >
        <span class="opportunity-stage chip" lang="de">${t(STAGE_KEY[o.stage])}</span>
        ${tierChip(o, t)}
      </div>
      <p class="opportunity-employer" lang="de">${o.employer}${o.location ? html` · ${o.location}` : nothing}</p>
      <div class="opportunity-chips">${verifyChips(o, t)}</div>
      ${leadLine(o, t)}
      ${orgDisclosure(o, t)}
      ${verdictActions(o, t)}
    </li>
  `
}
